import api from 'api';
import useAuth from 'hooks/useAuth';
import {get} from 'lodash';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';

type FormValues = {
  name: string;
  email: string;
  address: string;
  phone: string;
  id: string;
  website: string;
  company_email: string;
  company_phone: string;
};

function useUserModal({onClose}: {onClose: () => void}) {
  const {currentUser, logout, refetch} = useAuth();
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    register,
    formState: {errors},
    handleSubmit,
    setValue,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      name: get(currentUser, 'company.name', ''),
      email: get(currentUser, 'company.email', ''),
      address: get(currentUser, 'company.address', ''),
      company_email: get(currentUser, 'company.email', ''),
      company_phone: get(currentUser, 'company.phone', '')
        ? '(' +
          get(currentUser, 'company.phone', '')?.substring(0, 3) +
          ') ' +
          get(currentUser, 'company.phone', '')?.substring(3, 6) +
          '-' +
          get(currentUser, 'company.phone', '')?.substring(6, 10)
        : '',
      phone: get(currentUser, 'phone', '')
        ? '(' +
          get(currentUser, 'phone', '')?.substring(0, 3) +
          ') ' +
          get(currentUser, 'phone', '')?.substring(3, 6) +
          '-' +
          get(currentUser, 'phone', '')?.substring(6, 10)
        : '',
      id: get(currentUser, 'company.id', ''),
      website: get(currentUser, 'company.website', ''),
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const company_id = get(currentUser, 'company.id', 0);
      await api.put(`/companies/companies/${company_id}/`, {
        ...data,
        email: data.company_email,
        phone: data.company_phone.replace(/\D/g, ''),
        // company_phone: data.company_phone.replace(/\D/g, ''),
      });
      refetch(false);
      toast.success('Company details updated');
      onClose();
    } catch (error) {
      toast.error(get(error, 'response.data.error_message', '') || 'Error');
    }
  };

  const sendConfirmation = async () => {
    try {
      await api.post('/companies/send-code/', {
        email: getValues('email'),
      });
      toast.success('Confirmation code sent to your email');
      setConfirmationVisible(true);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
    }
  };

  const confirmHandler = async (value: string) => {
    try {
      await api.post('/companies/verify-code/', {
        code: value,
        email: getValues('email'),
      });

      handleSubmit(onSubmit)();
      setConfirmationVisible(false);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
      setLoading(false);
    }
  };

  const handleCheckChanges = async () => {
    try {
      if (getValues('email') === currentUser?.company?.email) {
        handleSubmit(onSubmit)();
      } else {
        await sendConfirmation();
      }
    } catch (error) {}
  };

  return {
    logout,
    currentUser,
    register,
    errors,
    handleSubmit,
    setValue,
    getValues,
    onSubmit,
    handleCheckChanges: handleSubmit(handleCheckChanges),
    confirmationVisible,
    setConfirmationVisible,
    loading,
    confirmHandler,
    sendConfirmation,
  };
}

export default useUserModal;
